import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Autocomplete,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout';
import { useDispatch } from 'react-redux';
import { createMeeting } from '../../store/slices/meetingsSlice';
import { getLeads, getLeadsStatus } from '../../store/slices/leadsSlice';
import { getUsersByRole } from '../../store/slices/userSlice';
import { extractList } from '../../utils/response';
import { toast } from 'react-toastify';
import { useAuth } from '../../hooks/useAuth';
import { CALENDAR_SCHEDULE_PATH, MEETING_VIEW_PATH } from '../../navigations/path';

const TIME_ZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'EST', label: 'EST' },
  { value: 'PST', label: 'PST' },
  { value: 'CST', label: 'CST' },
  { value: 'GMT', label: 'GMT' },
];

const CreateMeeting = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    lead_id: '',
    status_id: '',
    assignees: user?.id ? [user.id] : [],
    time_zone: 'UTC',
    start_date_time: '',
    end_date_time: '',
    description: '',
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [leadOptions, setLeadOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [assigneeOptions, setAssigneeOptions] = useState([]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        setLoading(true);
        const [leadsRes, statusRes, usersRes] = await Promise.all([
          dispatch(getLeads({ per_page: 200, paginate: 1 })).unwrap(),
          dispatch(getLeadsStatus({ 'conditions[model]': 'Meeting' })).unwrap(),
          dispatch(getUsersByRole('Sales User')).unwrap(),
        ]);
        const leadsList = extractList(leadsRes);
        setLeadOptions(
          (Array.isArray(leadsList) ? leadsList : []).map((l) => ({
            id: l.id ?? l.uuid,
            name: l.name || l.dba_name || l.email || 'Unknown',
            email: l.email,
          }))
        );
        const statusList = extractList(statusRes);
        setStatusOptions(Array.isArray(statusList) ? statusList : []);
        const usersList = Array.isArray(usersRes?.data) ? usersRes.data : extractList(usersRes);
        setAssigneeOptions(
          (Array.isArray(usersList) ? usersList : []).map((u) => ({
            id: u.uuid ?? u.id,
            name: u.name || u.username || 'Unknown',
          }))
        );
        if (formData.assignees.length === 0 && user?.id) {
          setFormData((prev) => ({ ...prev, assignees: [user.id] }));
        }
      } catch (err) {
        console.error('Error loading options:', err);
        toast.error('Failed to load form options');
      } finally {
        setLoading(false);
      }
    };
    loadOptions();
  }, [dispatch]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const next = {};
    if (!formData.lead_id) next.lead_id = 'Lead is required';
    if (!formData.status_id) next.status_id = 'Status is required';
    if (!formData.assignees?.length) next.assignees = 'At least one assignee is required';
    if (!formData.start_date_time) next.start_date_time = 'Start date & time is required';
    if (!formData.end_date_time) next.end_date_time = 'End date & time is required';
    if (formData.start_date_time && formData.end_date_time) {
      const start = new Date(formData.start_date_time).getTime();
      const end = new Date(formData.end_date_time).getTime();
      if (end <= start) next.end_date_time = 'End must be after start';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        lead_id: formData.lead_id,
        status_id: formData.status_id,
        assignees: formData.assignees,
        time_zone: formData.time_zone,
        start_date_time: formData.start_date_time,
        end_date_time: formData.end_date_time,
        description: formData.description?.trim() || '',
      };

      const res = await dispatch(createMeeting(payload)).unwrap();
      const meetingId = res?.data?.id ?? res?.id;
      toast.success('Meeting created successfully');
      if (meetingId) {
        navigate(`${MEETING_VIEW_PATH}/${meetingId}`);
      } else {
        navigate(CALENDAR_SCHEDULE_PATH);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to create meeting');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedLead = leadOptions.find((l) => l.id === formData.lead_id) || null;
  const selectedAssignees = assigneeOptions.filter((a) => formData.assignees?.includes(a.id)) || [];

  if (loading) {
    return (
      <PageLayout>
        <Container maxWidth="md" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Container>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(CALENDAR_SCHEDULE_PATH)} sx={{ mb: 2 }}>
          Back to Calendar
        </Button>

        <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
          Create Meeting
        </Typography>

        <Card variant="outlined">
          <CardContent>
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Autocomplete
                    options={leadOptions}
                    getOptionLabel={(opt) => (opt ? `${opt.name}${opt.email ? ` (${opt.email})` : ''}` : '')}
                    value={selectedLead}
                    onChange={(_, value) => handleChange('lead_id', value?.id ?? '')}
                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Lead"
                        required
                        error={!!errors.lead_id}
                        helperText={errors.lead_id}
                        placeholder="Search and select lead"
                      />
                    )}
                    noOptionsText="No leads found"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required error={!!errors.status_id}>
                    <InputLabel>Meeting Status</InputLabel>
                    <Select
                      value={formData.status_id}
                      onChange={(e) => handleChange('status_id', e.target.value)}
                      label="Meeting Status"
                    >
                      {statusOptions.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.status_id && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {errors.status_id}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    multiple
                    options={assigneeOptions}
                    getOptionLabel={(opt) => opt?.name ?? ''}
                    value={selectedAssignees}
                    onChange={(_, value) => handleChange('assignees', value?.map((v) => v.id) ?? [])}
                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Assignees"
                        
                        error={!!errors.assignees}
                        helperText={errors.assignees}
                        placeholder="Select assignees"
                      />
                    )}
                    noOptionsText="No users found"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Time Zone</InputLabel>
                    <Select
                      value={formData.time_zone}
                      onChange={(e) => handleChange('time_zone', e.target.value)}
                      label="Time Zone"
                    >
                      {TIME_ZONES.map((tz) => (
                        <MenuItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} />

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Start Date & Time"
                    type="datetime-local"
                    value={formData.start_date_time}
                    onChange={(e) => handleChange('start_date_time', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.start_date_time}
                    helperText={errors.start_date_time}
                    required
                    inputProps={{ min: new Date().toISOString().slice(0, 16) }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End Date & Time"
                    type="datetime-local"
                    value={formData.end_date_time}
                    onChange={(e) => handleChange('end_date_time', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.end_date_time}
                    helperText={errors.end_date_time}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={3}
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Meeting agenda or notes…"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={submitting ? <CircularProgress size={20} /> : <AddIcon />}
                      disabled={submitting}
                    >
                      {submitting ? 'Creating…' : 'Create Meeting'}
                    </Button>
                    <Button variant="outlined" onClick={() => navigate(CALENDAR_SCHEDULE_PATH)}>
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateMeeting;
